import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
  Image,
} from "@react-pdf/renderer";
import dayjs from "dayjs";
import { convertAmountToWords } from "./NumberWordHelper";

// ----------------------------------------------------------------------
// Register Font & Disable Hyphenation
// ----------------------------------------------------------------------
Font.register({
  family: "Manrope",
  fonts: [
    {
      src: "https://cdn.jsdelivr.net/fontsource/fonts/manrope@latest/latin-400-normal.ttf",
      fontWeight: "normal",
    },
    {
      src: "https://cdn.jsdelivr.net/fontsource/fonts/manrope@latest/latin-700-normal.ttf",
      fontWeight: "bold",
    },
  ],
});

Font.registerHyphenationCallback((word) => [word]);

// ----------------------------------------------------------------------
// Stylesheet Generator (Handles Dynamic Layout Heights)
// ----------------------------------------------------------------------
const createStyles = (showFooterOnAllPages) =>
  StyleSheet.create({
    page: {
      paddingTop: 20,
      paddingBottom: showFooterOnAllPages ? 120 : 40,
      paddingHorizontal: 25,
      fontSize: 8.5,
      fontFamily: "Manrope",
      color: "#222",
      position: "relative",
    },

    watermarkContainer: {
      position: "absolute",
      top: "32%",
      left: "20%",
      width: "60%",
      height: 250,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      opacity: 0.1,
      zIndex: -1,
    },
    watermarkImage: {
      width: "100%",
      height: "100%",
      objectFit: "contain",
    },

    // Header & Footer
    headerSpace: {
      width: "100%",
      height: 75,
    },
    header_image: {
      width: "100%",
      height: "100%",
      objectFit: "contain",
    },
    footerContainer: {
      position: "absolute",
      bottom: 15,
      left: 25,
      right: 25,
    },
    footer_image: {
      width: "100%",
      height: 50,
      objectFit: "contain",
    },

    contentWrapper: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
    },

    // Title Section
    titleContainer: {
      alignItems: "center",
      marginBottom: 10,
      paddingBottom: 4,
      marginTop: 8,
    },
    mainTitle: {
      fontSize: 15,
      fontWeight: "bold",
      textTransform: "uppercase",
      letterSpacing: 0.5,
    },
    titleSubText: {
      fontSize: 8.5,
      fontWeight: "bold",
      marginTop: 2,
      color: "#333",
    },

    // Split Metadata Box Layout
    splitInfoContainer: {
      flexDirection: "row",
      marginBottom: 10,
      paddingVertical: 4,
    },
    customerBox: {
      flex: 1.2,
      paddingRight: 10,
    },
    invoiceDetailsBox: {
      flex: 1,
    },
    metaRow: {
      flexDirection: "row",
      minHeight: 18,
      alignItems: "center",
    },
    metaRowLast: {
      flexDirection: "row",
      minHeight: 18,
      alignItems: "center",
    },
    metaLabelCell: {
      width: "45%",
      paddingVertical: 2,
      fontSize: 7,
      color: "#201f1fff",
      fontWeight: "bold",
      textTransform: "uppercase",
    },
    metaValueCell: {
      width: "55%",
      paddingVertical: 2,
      fontSize: 8,
      fontWeight: "bold",
    },

    label: {
      fontSize: 7,
      color: "#201f1fff",
      textTransform: "uppercase",
      marginBottom: 3,
      fontWeight: "bold",
    },
    value: {
      fontSize: 8.5,
      fontWeight: "bold",
    },

    fullWidthBox: {
      width: "100%",
      paddingVertical: 4,
      marginBottom: 10,
    },

    // TABLE STYLES
    tableContainer: {
      width: "100%",
      flex: 1,
      display: "flex",
      flexDirection: "column",
      marginBottom: 10,
    },
    tableHeader: {
      flexDirection: "row",
      backgroundColor: "#f2f2f2",
      paddingVertical: 6,
      borderRadius: 2,
      alignItems: "center",
    },
    tableBodyContainer: {
      flex: 1,
    },
    tableRow: {
      flexDirection: "row",
      paddingVertical: 4,
      minHeight: 18,
      alignItems: "center",
      borderBottomWidth: 0,
    },
    cell: {
      paddingHorizontal: 4,
      fontSize: 8,
    },
    cellRight: {
      paddingHorizontal: 4,
      fontSize: 8,
      textAlign: "right",
    },

    // Column Sizes
    colSl: { width: "5%", textAlign: "center" },
    colCode: { width: "12%" },
    cellCodeText: {
      fontSize: 7.5,
    },
    colDescWithUnit: { width: "35%" },
    colDescFull: { width: "42%" },
    colUnit: { width: "6%", textAlign: "center" },
    colQty: { width: "5%", textAlign: "center" },
    colUnitPrice: { width: "10%", textAlign: "right" },
    colExclVat: { width: "12%", textAlign: "right" },
    colVatAmt: { width: "11%", textAlign: "right" },
    colTotal: { width: "13%", textAlign: "right" },

    // Summary Container
    summarySectionWrapper: {
      width: "100%",
      borderTopWidth: 1,
      borderTopColor: "#e0e0e0",
      paddingTop: 8,
    },
    summaryContainer: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "flex-start",
      marginBottom: 15,
      paddingTop: 4,
    },
    wordsSideBox: {
      width: "48%",
      paddingRight: 15,
    },
    totalsBox: {
      width: "50%",
    },
    totalRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      marginBottom: 4,
    },
    totalLabel: {
      fontSize: 7.5,
      color: "#444",
      fontWeight: "semibold",
    },
    totalValue: {
      fontSize: 8,
      fontWeight: "bold",
    },

    // Fixed Bottom Signature Section
    signatureSection: {
      position: "absolute",
      bottom: showFooterOnAllPages ? 70 : 20,
      left: 25,
      right: 25,
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "flex-end",
    },
    signatureCol: {
      width: "35%",
      alignItems: "center",
    },
    signatureLine: {
      width: "100%",
      borderBottomWidth: 1,
      borderBottomColor: "#000",
      marginBottom: 4,
    },
    signatureLabel: {
      fontSize: 8,
      fontWeight: "bold",
      textAlign: "center",
    },
    sealAndSignatureWrapper: {
      flexDirection: "row",
      alignItems: "flex-end",
      justifyContent: "center",
      marginBottom: 3,
      gap: 6,
    },
    sealImage: {
      width: 45,
      height: 45,
      objectFit: "contain",
    },
    signatureImage: {
      width: 60,
      height: 35,
      objectFit: "contain",
    },
    pageNumberTextTop: {
      position: "absolute",
      bottom: showFooterOnAllPages ? 68 : 18,
      left: 0,
      right: 0,
      textAlign: "center",
      fontSize: 8,
      color: "#444",
    },
  });

const CubixInvoicePrint2PDF = ({
  invoiceData = [],
  REPORT_FOOTER_IMAGE,
  REPORT_HEADER_IMAGE,
  COMPANY_NAME,
  reportSealImage,
  reportWatermarkImage,
  reportSignatureImage,
  showHeader = true,
  showFooter = true,
  showWatermark = true,
  showSeal = true,
  showSignature = true,
  showCode = false,
  showFOC = false,
  showHeaderOnAllPages = true,
  showFooterOnAllPages = true,
  showInvoiceInfoOnAllPages = true,
}) => {
  const styles = createStyles(showFooterOnAllPages);

  const CURRENT_USER_OBJECT = localStorage.getItem("currentUser")
    ? JSON.parse(localStorage.getItem("currentUser"))
    : [];

  const currentUserData = CURRENT_USER_OBJECT[0] || {};
  const VATPERCENTAGE = currentUserData?.vatp;

  const userSignatureBase64 =
    currentUserData?.SIGNATURE ||
    currentUserData?.sign ||
    currentUserData?.user_signature ||
    null;

  if (!invoiceData || invoiceData.length === 0) {
    return <Document />;
  }

  const firstItem = invoiceData[0];
  const isVatApplied = firstItem?.deliv_site === "VAT Applied";
  const vatPercent = isVatApplied ? Number(VATPERCENTAGE || 0) : 0;
  const companyTRN = currentUserData?.trn || currentUserData?.TRN || "-";

  const activeCompanyName =
    COMPANY_NAME || currentUserData?.cmp_name || "COMPANY";

  const isFOCItem = (item) =>
    (item.unit || item.uom || "").toUpperCase() === "FOC";

  const filteredData = showFOC
    ? invoiceData
    : invoiceData.filter((item) => !isFOCItem(item));

  const calcData = filteredData;

  const hasUnitColumn = filteredData.some(
    (item) => (item.unit || item.uom || "").trim() !== "",
  );
  const hasCodeColumn =
    showCode &&
    filteredData.some(
      (item) => (item.code || item.tr_code || "").trim() !== "",
    );

  const lineItems = filteredData.map((item, index) => {
    const qty = item.tr_qty || 0;
    const unitPrice = item.funit_pric || 0;
    const lineTotalExclVat = qty * unitPrice;
    const lineVat = Math.round(lineTotalExclVat * vatPercent) / 100;
    const lineTotalWithVat = lineTotalExclVat + lineVat;

    return {
      slNo: index + 1,
      code: item.code || item.tr_code || "",
      desc: item.idesc || item.tr_desc || "-",
      uom: item.unit || item.uom || "",
      qty: qty.toString(),
      unitPrice: unitPrice.toFixed(2),
      exclVatAmount: lineTotalExclVat.toFixed(2),
      vatAmount: lineVat.toFixed(2),
      amount: lineTotalWithVat.toFixed(2),
    };
  });

  const totalExclVat = calcData.reduce(
    (sum, item) => sum + (item.funit_pric || 0) * (item.tr_qty || 0),
    0,
  );
  const specialDiscount = firstItem?.disc_amt || firstItem?.fdisc_amt || 0;
  const subTotal = totalExclVat - specialDiscount;
  const roundOffValue = Number(firstItem?.e) || 0;
  const advancePercent = Number(firstItem?.paid_amt || 0);

  let vatAmount = 0;
  let grandTotal = 0;
  let advanceReceived = 0;

  if (advancePercent > 0) {
    advanceReceived = subTotal * (advancePercent / 100);
    vatAmount = isVatApplied
      ? Math.round(advanceReceived * vatPercent) / 100
      : 0;
    grandTotal = advanceReceived + vatAmount + roundOffValue;
  } else {
    vatAmount = isVatApplied ? Math.round(subTotal * vatPercent) / 100 : 0;
    grandTotal =
      Number(subTotal?.toFixed(2)) +
      Number(vatAmount?.toFixed(2)) +
      Number(roundOffValue?.toFixed(2));
  }
  console.log("grandTotal", grandTotal);
  const amountInWords = convertAmountToWords(
    grandTotal,
    firstItem?.fc || "AED",
  );

  const rawAddress = [firstItem?.address1, firstItem?.address2]
    .filter((addr) => addr && addr.trim() !== "")
    .join(", ");

  const formatCurrency = (val) =>
    Number(val || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const manipulateTextToMakenewLine = (stringReceived) => {
    let n = 10;
    let insertChar = "\n";
    let outputString = stringReceived?.split("").reduce(function (acc, val, i) {
      return i % n === 0 && i !== 0 ? acc + insertChar + val : acc + val;
    }, "");

    return outputString;
  };

  const colDescStyle = hasCodeColumn
    ? hasUnitColumn
      ? { width: "28%" }
      : { width: "35%" }
    : hasUnitColumn
      ? styles.colDescWithUnit
      : styles.colDescFull;

  // Header Component Render Logic
  const renderHeaderImage = () => {
    if (!showHeader || !REPORT_HEADER_IMAGE) return null;
    return <Image src={REPORT_HEADER_IMAGE} style={styles.header_image} />;
  };

  // Title + TRN - Always shown on every page (fixed)
  const renderTitleSection = () => (
    <View style={styles.titleContainer}>
      <Text style={styles.mainTitle}>
        {isVatApplied ? "Tax Invoice" : "Commercial Invoice"}
      </Text>
      {companyTRN !== "-" && (
        <Text style={styles.titleSubText}>TRN: {companyTRN}</Text>
      )}
    </View>
  );

  // Invoice Meta Section Render Logic (customer + invoice details)
  const renderInvoiceInfo = () => (
    <View style={styles.splitInfoContainer}>
      {/* LEFT: Customer Section */}
      <View style={styles.customerBox}>
        <Text style={styles.label}>Billed To / Customer</Text>
        <Text style={[styles.value, { fontSize: 9.5, marginBottom: 4 }]}>
          {firstItem?.custref || firstItem?.tr_desc || "Client Name"}
        </Text>

        {rawAddress ? (
          <Text
            style={[
              styles.value,
              { fontWeight: "normal", fontSize: 8, color: "#444" },
            ]}
          >
            {rawAddress}
          </Text>
        ) : null}
        <View style={{ marginTop: 3 }}>
          <Text style={styles.value}>{firstItem?.tel || "-"}</Text>
        </View>
        <View style={{ marginTop: 6 }}>
          <Text style={styles.label}>Customer TRN</Text>
          <Text style={styles.value}>{firstItem?.governor || "-"}</Text>
        </View>
      </View>

      {/* RIGHT: Invoice Meta Grid */}
      <View style={styles.invoiceDetailsBox}>
        <View style={styles.metaRow}>
          <View style={styles.metaLabelCell}>
            <Text>Invoice No</Text>
          </View>
          <View style={styles.metaValueCell}>
            <Text>{firstItem?.inv_no || "-"}</Text>
          </View>
        </View>

        <View style={styles.metaRow}>
          <View style={styles.metaLabelCell}>
            <Text>Date</Text>
          </View>
          <View style={styles.metaValueCell}>
            <Text>
              {firstItem?.inv_date
                ? dayjs(firstItem?.inv_date).format("DD-MM-YYYY")
                : "-"}
            </Text>
          </View>
        </View>
        {firstItem?.lpo_no && (
          <View style={styles.metaRow}>
            <View style={styles.metaLabelCell}>
              <Text>LPO No</Text>
            </View>
            <View style={styles.metaValueCell}>
              <Text>{firstItem?.lpo_no || "-"}</Text>
            </View>
          </View>
        )}
        {firstItem?.do_data?.length > 0 && (
          <View style={styles.metaRow}>
            <View style={styles.metaLabelCell}>
              <Text>DO No</Text>
            </View>
            <View
              style={[styles.metaValueCell, { flexShrink: 1, minWidth: 0 }]}
            >
              <Text style={{ fontSize: 8, fontWeight: "bold" }}>
                {firstItem?.do_data
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean)
                  .join(", ")}
              </Text>
            </View>
          </View>
        )}

        <View style={styles.metaRowLast}>
          <View style={styles.metaLabelCell}>
            <Text>Salesman</Text>
          </View>
          <View style={styles.metaValueCell}>
            <Text>{firstItem?.sale_man || "-"}</Text>
          </View>
        </View>
      </View>
    </View>
  );

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Background Watermark */}
        {showWatermark && reportWatermarkImage && (
          <View style={styles.watermarkContainer} fixed>
            <Image src={reportWatermarkImage} style={styles.watermarkImage} />
          </View>
        )}

        {/* Header Image */}
        {showHeaderOnAllPages ? (
          <View style={styles.headerSpace} fixed>
            {renderHeaderImage()}
          </View>
        ) : (
          <View style={styles.headerSpace}>{renderHeaderImage()}</View>
        )}

        <View style={styles.contentWrapper}>
          {/* Title + TRN - Always fixed on every page */}
          <View fixed>{renderTitleSection()}</View>

          {/* Invoice Info (customer + details) - Controlled by showInvoiceInfoOnAllPages */}
          {showInvoiceInfoOnAllPages ? (
            <View fixed>{renderInvoiceInfo()}</View>
          ) : (
            <View>{renderInvoiceInfo()}</View>
          )}

          {/* Table Container */}
          <View style={styles.tableContainer}>
            <View style={styles.tableHeader} fixed>
              <Text style={[styles.cell, styles.colSl, { fontWeight: "bold" }]}>
                SL No
              </Text>
              {hasCodeColumn && (
                <Text
                  style={[styles.cell, styles.colCode, { fontWeight: "bold" }]}
                >
                  Code
                </Text>
              )}
              <Text style={[styles.cell, colDescStyle, { fontWeight: "bold" }]}>
                Description
              </Text>
              {hasUnitColumn && (
                <Text
                  style={[styles.cell, styles.colUnit, { fontWeight: "bold" }]}
                >
                  Unit
                </Text>
              )}
              <Text
                style={[styles.cell, styles.colQty, { fontWeight: "bold" }]}
              >
                Qty
              </Text>
              <Text
                style={[
                  styles.cellRight,
                  styles.colUnitPrice,
                  { fontWeight: "bold" },
                ]}
              >
                Unit Price
              </Text>

              <Text
                style={[
                  styles.cellRight,
                  styles.colExclVat,
                  { fontWeight: "bold" },
                ]}
              >
                {"Excl. VAT\nAmount"}
              </Text>

              {isVatApplied && (
                <Text
                  style={[
                    styles.cellRight,
                    styles.colVatAmt,
                    { fontWeight: "bold" },
                  ]}
                >
                  {`VAT @\n${vatPercent}%`}
                </Text>
              )}

              <Text
                style={[
                  styles.cellRight,
                  styles.colTotal,
                  { fontWeight: "bold" },
                ]}
              >
                {"Total\n(Incl VAT)"}
              </Text>
            </View>

            <View style={styles.tableBodyContainer}>
              {lineItems.map((item, idx) => (
                <View style={styles.tableRow} key={idx}>
                  <Text style={[styles.cell, styles.colSl]}>{item.slNo}</Text>
                  {hasCodeColumn && (
                    <View style={[styles.cell, styles.colCode]}>
                      <Text style={styles.cellCodeText}>
                        {manipulateTextToMakenewLine(item?.code)}
                      </Text>
                    </View>
                  )}
                  <Text style={[styles.cell, colDescStyle]}>{item.desc}</Text>
                  {hasUnitColumn && (
                    <Text style={[styles.cell, styles.colUnit]}>
                      {item.uom}
                    </Text>
                  )}
                  <Text style={[styles.cell, styles.colQty]}>{item.qty}</Text>
                  <Text style={[styles.cellRight, styles.colUnitPrice]}>
                    {item.unitPrice}
                  </Text>
                  <Text style={[styles.cellRight, styles.colExclVat]}>
                    {item.exclVatAmount}
                  </Text>
                  {isVatApplied && (
                    <Text style={[styles.cellRight, styles.colVatAmt]}>
                      {item.vatAmount}
                    </Text>
                  )}
                  <Text style={[styles.cellRight, styles.colTotal]}>
                    {item.amount}
                  </Text>
                </View>
              ))}
            </View>
          </View>

          {/* Combined Summary Section */}
          <View
            render={({ pageNumber, totalPages }) => {
              if (pageNumber !== totalPages) return null;
              return (
                <View style={styles.summarySectionWrapper} wrap={false}>
                  <View style={styles.summaryContainer}>
                    <View style={styles.wordsSideBox}>
                      <Text
                        style={[
                          styles.value,
                          { fontSize: 8.5, color: "#333", marginBottom: 10 },
                        ]}
                      >
                        {amountInWords}
                      </Text>

                      {firstItem?.terms && firstItem?.terms !== "0" && (
                        <View>
                          <Text style={styles.label}>Remarks / Terms</Text>
                          <Text
                            style={[
                              styles.value,
                              {
                                fontWeight: "normal",
                                fontSize: 8,
                                color: "#444",
                              },
                            ]}
                          >
                            {firstItem?.terms}
                          </Text>
                        </View>
                      )}
                    </View>

                    <View style={styles.totalsBox}>
                      <View style={styles.totalRow}>
                        <Text style={styles.totalLabel}>
                          {"Total\n(Excl. VAT)"}
                        </Text>
                        <Text style={styles.totalValue}>
                          {formatCurrency(totalExclVat)}
                        </Text>
                      </View>

                      {specialDiscount > 0 && (
                        <View style={styles.totalRow}>
                          <Text style={styles.totalLabel}>
                            Special Discount
                          </Text>
                          <Text style={styles.totalValue}>
                            {formatCurrency(specialDiscount)}
                          </Text>
                        </View>
                      )}

                      {specialDiscount > 0 && (
                        <View style={styles.totalRow}>
                          <Text style={styles.totalLabel}>Sub Total</Text>
                          <Text style={styles.totalValue}>
                            {formatCurrency(subTotal)}
                          </Text>
                        </View>
                      )}

                      {advanceReceived > 0 && (
                        <View style={styles.totalRow}>
                          <Text style={styles.totalLabel}>
                            {`Advance Amount ${advancePercent}%`}
                          </Text>
                          <Text style={styles.totalValue}>
                            {formatCurrency(advanceReceived)}
                          </Text>
                        </View>
                      )}

                      {isVatApplied && (
                        <View style={styles.totalRow}>
                          <Text style={styles.totalLabel}>
                            VAT ({vatPercent}%)
                          </Text>
                          <Text style={styles.totalValue}>
                            {formatCurrency(vatAmount)}
                          </Text>
                        </View>
                      )}

                      {roundOffValue !== 0 && (
                        <View style={styles.totalRow}>
                          <Text style={styles.totalLabel}>Round Off</Text>
                          <Text style={styles.totalValue}>
                            {formatCurrency(roundOffValue)}
                          </Text>
                        </View>
                      )}

                      <View
                        style={[
                          styles.totalRow,
                          {
                            paddingTop: 4,
                            marginTop: 2,
                            borderTopWidth: 1,
                            borderTopColor: "#eee",
                          },
                        ]}
                      >
                        <Text
                          style={[
                            styles.totalLabel,
                            { fontWeight: "bold", color: "#000" },
                          ]}
                        >
                          GRAND TOTAL ({firstItem?.fc || "AED"})
                        </Text>
                        <Text
                          style={[
                            styles.totalValue,
                            { fontSize: 9.5, color: "#000" },
                          ]}
                        >
                          {formatCurrency(grandTotal)}
                        </Text>
                      </View>
                    </View>
                  </View>
                </View>
              );
            }}
          />
        </View>

        {/* Page Number */}
        <Text
          style={styles.pageNumberTextTop}
          render={({ pageNumber, totalPages }) => {
            if (pageNumber !== totalPages) return null;
            return `Page ${pageNumber} of ${totalPages}`;
          }}
        />

        {/* Fixed Bottom Signature Section */}
        <View
          style={styles.signatureSection}
          render={({ pageNumber, totalPages }) => {
            if (pageNumber !== totalPages) return null;
            return (
              <>
                <View style={styles.signatureCol}>
                  <View style={styles.signatureLine} />
                  <Text style={styles.signatureLabel}>
                    Receiver's Signature
                  </Text>
                </View>

                <View style={styles.signatureCol}>
                  {(showSignature || showSeal) && (
                    <View style={styles.sealAndSignatureWrapper}>
                      {showSignature && userSignatureBase64 ? (
                        <Image
                          src={
                            userSignatureBase64.startsWith("data:")
                              ? userSignatureBase64
                              : `data:image/png;base64,${userSignatureBase64}`
                          }
                          style={styles.signatureImage}
                        />
                      ) : null}

                      {showSeal && reportSealImage ? (
                        <Image src={reportSealImage} style={styles.sealImage} />
                      ) : null}
                    </View>
                  )}

                  <View style={styles.signatureLine} />
                  <Text style={styles.signatureLabel}>
                    For {activeCompanyName}
                  </Text>
                </View>
              </>
            );
          }}
        />

        {/* Footer Image Container */}
        {showFooter && REPORT_FOOTER_IMAGE && (
          <View
            style={styles.footerContainer}
            fixed={showFooterOnAllPages}
            render={({ pageNumber, totalPages }) => {
              if (!showFooterOnAllPages && pageNumber !== 1) return null;
              return (
                <Image src={REPORT_FOOTER_IMAGE} style={styles.footer_image} />
              );
            }}
          />
        )}
      </Page>
    </Document>
  );
};

export default CubixInvoicePrint2PDF;
